import { SalesTaxFilingStatusEnum } from '@prisma/client';

import {
  IndirectTaxFilingStatus,
  IndirectTaxFilingTableFilter,
} from '../types';
import { IndirectTaxFilingsHelper } from './IndirectTaxFilingsHelper';

export class IndirectTaxFilingDashboardHelper {
  // Cursor history sentinel used to distinguish "go back to first page"
  // (null cursor) from "no history" during navigation.
  static readonly CURSOR_HISTORY_FIRST_SENTINEL = '__first__';

  static readonly JURISDICTION_FILTER_KEY =
    IndirectTaxFilingTableFilter.JURISDICTION;

  static readonly FILING_FREQUENCY_FILTER_KEY =
    IndirectTaxFilingTableFilter.FILING_FREQUENCY;

  static computeEffectiveJurisdictionIds(
    selectedJurisdictionIds: string[] | undefined,
    isAllRegions: boolean,
    regionJurisdictionIds: string[]
  ): string[] | undefined {
    if (selectedJurisdictionIds && selectedJurisdictionIds.length > 0) {
      return !isAllRegions
        ? selectedJurisdictionIds.filter((id) =>
            regionJurisdictionIds.includes(id)
          )
        : selectedJurisdictionIds;
    }
    return isAllRegions ? undefined : regionJurisdictionIds;
  }

  static computeEffectiveStatuses(
    activeTab: IndirectTaxFilingStatus | 'ALL'
  ): SalesTaxFilingStatusEnum[] | undefined {
    if (activeTab === 'ALL') return undefined;
    return [IndirectTaxFilingsHelper.toApiStatus(activeTab)];
  }

  // The status tab persists across periods and reloads, so the filtered list
  // can be empty while other statuses still have filings. Any non-zero
  // per-status count keeps the tabs visible so the user can switch away.
  static hasAnyFilings(
    listCount: number,
    listRowCount: number,
    statusCounts: Readonly<Partial<Record<SalesTaxFilingStatusEnum, number>>>
  ): boolean {
    if (listCount > 0 || listRowCount > 0) return true;
    return Object.values(statusCounts).some((count) => (count ?? 0) > 0);
  }

  static buildInitialFilingPeriod(): { fromDate: string; toDate: string } {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    const monthStr = String(month).padStart(2, '0');
    return {
      fromDate: `${year}-${monthStr}-01`,
      toDate: `${year}-${monthStr}-${String(lastDay).padStart(2, '0')}`,
    };
  }

  static pushCursorHistory(
    history: Readonly<string[]>,
    currentCursor: string | null
  ): string[] {
    return [
      ...history,
      currentCursor ??
        IndirectTaxFilingDashboardHelper.CURSOR_HISTORY_FIRST_SENTINEL,
    ];
  }

  static popCursorHistory(history: Readonly<string[]>): {
    history: string[];
    cursor: string | null;
  } {
    const next = [...history];
    const prev = next.pop();
    const cursor =
      prev === IndirectTaxFilingDashboardHelper.CURSOR_HISTORY_FIRST_SENTINEL
        ? null
        : (prev ?? null);
    return { history: next, cursor };
  }

  static getJurisdictionFilterIds(
    filters: Record<string, string | string[]>
  ): string[] | undefined {
    const value =
      filters[IndirectTaxFilingDashboardHelper.JURISDICTION_FILTER_KEY];
    if (!value) return undefined;
    return Array.isArray(value) ? value : [value];
  }

  static getFrequencyFilterValues(
    filters: Record<string, string | string[]>
  ): string[] | undefined {
    const value =
      filters[IndirectTaxFilingDashboardHelper.FILING_FREQUENCY_FILTER_KEY];
    if (!value) return undefined;
    return Array.isArray(value) ? value : [value];
  }
}
